import { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";

const DISMISS_KEY = "ws_pwa_install_dismissed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}

export default function InstallPwaButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosHint, setIosHint] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setHidden(true);
      return;
    }
    if (localStorage.getItem(DISMISS_KEY) === "1") {
      setHidden(true);
      return;
    }
    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    // iOS has no beforeinstallprompt — show the Add-to-Home-Screen hint instead.
    if (isIos()) setIosHint(true);
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  if (hidden) return null;
  if (!deferredPrompt && !iosHint) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {
        /* user dismissed */
      }
      setDeferredPrompt(null);
      setHidden(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setHidden(true);
  };

  return (
    <div
      data-testid="install-pwa-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-[640px] w-[calc(100%-1rem)] bg-[#09090B] text-white border-2 border-[#FFCC00] ws-hard-shadow px-4 py-3 flex items-center gap-3 print:hidden"
    >
      <div className="w-9 h-9 bg-[#FF5A00] border-2 border-[#FFCC00] flex items-center justify-center shrink-0">
        <Smartphone strokeWidth={2.5} className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="ws-label text-[#FFCC00]">Install the app</div>
        {iosHint && !deferredPrompt ? (
          <div className="text-sm text-white mt-0.5">
            Tap <strong>Share</strong> then <strong>Add to Home Screen</strong> to install worksafestory.com.
          </div>
        ) : (
          <div className="text-sm text-white mt-0.5 truncate">
            Add worksafestory.com to your home screen — opens like a real app, no app store needed.
          </div>
        )}
      </div>
      {deferredPrompt && (
        <button
          type="button"
          onClick={handleInstall}
          data-testid="install-pwa-confirm-btn"
          className="bg-[#FFCC00] text-[#09090B] ws-label px-4 py-2 border-2 border-[#FFCC00] hover:bg-white transition-colors"
        >
          Install
        </button>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        data-testid="install-pwa-dismiss-btn"
        className="w-9 h-9 flex items-center justify-center text-white hover:text-[#FFCC00] transition-colors shrink-0"
      >
        <X strokeWidth={2.5} className="w-4 h-4" />
      </button>
    </div>
  );
}
